import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { Popover, useTheme } from '@mui/material';
import {
  MapSupportCheckProvider,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { LayoutFullWidth } from '@product-framework/index';
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import AssignJobDialog from '@product-framework/components/Dialogs/AssignJobDialog';
import SendMessageDialog from '@product-framework/components/Dialogs/SendMessageDialog';
import CancelJobDialog from '@product-framework/components/Dialogs/CancelJobDialog';
import RescheduleJobDialog from '@product-framework/components/Dialogs/RescheduleJobDialog';
import getClosestDateItemFromToday from '@ui-framework/helpers/getClosestDateItemFromToday';
import JobMap from '../../components/Map';
import MapLib from '../../components/Map/lib/MapLib';
import CenterMapButton from '../../components/Map/buttons/CenterMapButton';
import BackButton from '../../components/Map/buttons/BackButton';
import JobAndSPWidget from '../../components/Map/widget/index';
import ServiceProviderInfo from '../../components/Map/widget/ServiceProviderInfo';
import LegendsWidget from '../../components/Map/widget/Legends';

const SORT_OPTIONS = formatMessage => [
  {
    value: 'dispatchTime:ASC',
    label: formatMessage({
      id: 'DISPATCH_TIME',
      defaultMessage: 'Dispatch Time',
    }),
  },
  {
    value: 'distance:ASC',
    label: formatMessage({
      id: 'CLOSEST_DISTANCE',
      defaultMessage: 'Closest Distance',
    }),
  },
  {
    value: 'name:ASC',
    label: formatMessage({
      id: 'ALPHABETICAL_A_TO_Z',
      defaultMessage: 'Alphabetical A → Z',
    }),
  },
  {
    value: 'name:DESC',
    label: formatMessage({
      id: 'ALPHABETICAL_Z_TO_A',
      defaultMessage: 'Alphabetical Z → A',
    }),
  },
];

function ServiceJobMap() {
  const theme = useTheme();
  const { id: currentSJId, locationId: locationIdFromUrl } = useParams();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, get, readStreamResult } = useDataProvider(provider);
  const snackbar = useSnackbar();
  const mapRef = useRef(null);
  const locationIdFromUrlProcessedRef = useRef(false);
  const { formatMessage } = useLocalization();

  const defaults = {
    zoom: 10,
    speed: 8,
    lat: 40.56378846993402,
    lng: -99.9599284137568,
  };

  const northAmericaBounds = [
    [-141.0, 24.0], // Southwest [lng, lat]
    [-52.0, 83.0], // Northeast [lng, lat]
  ];

  const defaultPagination = { limit: 10, page: 1, total: 0 };

  const dispatchStatusConfig = [
    {
      value: 'ALL',
      label: formatMessage({
        id: 'ALL_STATUS_FILTER',
        defaultMessage: 'All Providers',
      }),
    },
    {
      value: 'ACCEPTED_WITH_CONDITIONS',
      label: formatMessage({
        id: 'ACCEPTED_CONDITIONALLY_FILTER',
        defaultMessage: 'Accepted Conditionally',
      }),
    },
  ];

  const [map, setMap] = useState(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [spLoading, setSpLoading] = useState(true);
  const [jobInfo, setJobInfo] = useState(null);
  const [selectedServiceProvider, setSelectedServiceProvider] = useState(null);
  const [spPopOver, setSPPopOver] = useState({ open: false, top: 0, left: 0 });
  const [serviceProviders, setServiceProviders] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [dispatchEventStatus, setDispatchEventStatus] = useState(
    dispatchStatusConfig[0].value,
  );
  const [serviceDate, setServiceDate] = useState(dayjs());
  const [pagination, setPagination] = useState(defaultPagination);
  const [sortOption, setSortOption] = useState(
    SORT_OPTIONS(formatMessage)[0].value,
  );
  const [dialog, setDialog] = useState('NONE');
  const [mapId, setMapId] = useState('initial');

  const jobFetchUrl = `${getApiUrl()}/sxcl/v1/service-jobs/${currentSJId}`;

  const getServiceProvidersFetchUrl = () => {
    const sort = sortOption.split(':');
    let url = `${getApiUrl()}/sxcl/v1/service-jobs/${currentSJId}/map?serviceDate=${dayjs(
      serviceDate,
    ).format(
      'YYYY-MM-DD',
    )}&radius=160934&sortBy=${sort?.[0]}&sortOrder=${sort?.[1] || 'DESC'}`;

    // Append this when pagination is needed
    // & limit=${ defaultPagination?.limit }& page=${ pagination?.page }`;

    if (searchText && searchText.length > 1) {
      url += `&name=${encodeURIComponent(searchText)}`;
    }

    // Note: Leaving this code in when we need to filter new sp's by dispatchEventStatus
    // Also add dispatchEventStatus to useEffect Dependencies for renderServiceProvider
    if (
      dispatchEventStatus &&
      dispatchEventStatus !== dispatchStatusConfig[0].value
    ) {
      url += `&dispatchEventStatus=${dispatchEventStatus}`;
    }
    return url;
  };

  const isAssignedOrAcceptedSP = (sp, location) => {
    const dispatchStatus = sp?.metadata?.dispatchStatus;
    const isAssigned = dispatchStatus === 'ASSIGNED';
    const isAccepted = dispatchStatus === 'ACCEPTED';

    if (!isAssigned && !isAccepted) {
      return false;
    }

    if (location) {
      if (isAssigned) {
        return (
          Boolean(sp?.metadata?.assignedLocationId) &&
          location?.id === sp?.metadata?.assignedLocationId
        );
      }

      if (isAccepted) {
        return (
          Boolean(sp?.metadata?.acceptedLocationId) &&
          location?.id === sp?.metadata?.acceptedLocationId
        );
      }
    }

    return isAssigned || isAccepted;
  };

  const onServiceProviderClick = ({ sp, location }) => {
    let index = 0;
    if (location?.address?.lat && location?.address?.lng) {
      mapRef.current.adjustMapForLocation({
        location: [location?.address?.lng, location?.address?.lat],
      });
    }

    const marker = document?.getElementById(
      MapLib.serviceProviderGetMarkerId(sp?.id, location?.id),
    );

    if (marker) {
      mapRef.current.marker.selectedServiceProvider = marker;
      index = marker.getAttribute('data-index');
      const layerName = MapLib.serviceProviderServiceJobGetLayerName(index);

      mapRef.current.serviceProviderUnHighlightAllMarkers([sp?.id]);
      mapRef.current.serviceProviderServiceJobUnHighlightAllLayers([sp?.id]);
      mapRef.current.serviceProviderServiceJobHighlightLayer(layerName);
    }
    MapLib.serviceProviderHighlightMarker(sp?.id, location.id);

    setSelectedServiceProvider({
      sp: mapRef?.current.serviceProviders.find(spItem => spItem.id === sp?.id),
      location,
      index,
    });
    // Left and Right is not used as we used fixed position for pop over
    setSPPopOver({
      open: true,
      left: 0,
      top: 0,
    });
  };

  const closeServiceProviderPopover = () => {
    if (spPopOver?.open) {
      setSPPopOver({
        open: false,
        left: 0,
        top: 0,
      });

      const index =
        mapRef.current.marker.selectedServiceProvider?.getAttribute(
          'data-index',
        );
      const layerName = MapLib.serviceProviderServiceJobGetLayerName(index);
      mapRef?.current?.serviceProviderServiceJobUnHighlightLayer(layerName);
      mapRef.current.marker.selectedServiceProvider
        ?.getElementById('outer')
        ?.setAttribute('display', 'none');
    }
  };

  const fetchAndSetServiceJob = async () => {
    setJobLoading(true);
    const sjResponse = await get(jobFetchUrl);
    if (sjResponse.ok) {
      const currentJobInfo = sjResponse?.data;
      setJobInfo(currentJobInfo);
      const closestDate = getClosestDateItemFromToday({
        dates: currentJobInfo?.customerServiceRequests,
      })?.requestDate;

      let newServiceDate;
      if (currentJobInfo?.serviceJobStatus === 'SERVICE_PENDING') {
        newServiceDate = dayjs(currentJobInfo?.appointmentDate);
      } else if (closestDate && dayjs(closestDate).isValid()) {
        newServiceDate = dayjs(closestDate);
      } else {
        newServiceDate = dayjs();
      }
      setServiceDate(newServiceDate);
      mapRef?.current?.setJob({ job: currentJobInfo });
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={sjResponse?.errors} />,
      });
    }
    setJobLoading(false);
  };

  const fetchRouteForAssignedOrAcceptedServiceProvider = async () => {
    const response = await readStreamResult(
      `${getApiUrl()}/sxcl/v1/service-jobs/${currentSJId}/map/route`,
    );
    return response;
  };

  const fetchServiceProviderData = async () => {
    const result = {
      serviceProviders: [],
      pagination: defaultPagination,
      routeGeometry: null,
    };
    const serviceProviderFetchUrl = getServiceProvidersFetchUrl();
    const spResponse = await get(serviceProviderFetchUrl);
    if (spResponse.ok) {
      const { data, ...paginationData } = spResponse.data;
      result.serviceProviders = data;
      // Have to apply this, since api is sending page only total, and if page is missing component renders twice
      result.pagination = {
        ...paginationData,
        page: pagination?.page || defaultPagination.page,
      };

      if (Array.isArray(data)) {
        const assignedOrAcceptedSPExists = data?.some(sp =>
          isAssignedOrAcceptedSP(sp),
        );

        if (assignedOrAcceptedSPExists) {
          const spRouteResponse =
            await fetchRouteForAssignedOrAcceptedServiceProvider({
              serviceProviders: data,
            });
          if (spRouteResponse?.ok) {
            if (spRouteResponse?.data?.routeGeometry?.coordinates) {
              result.routeGeometry =
                spRouteResponse?.data?.routeGeometry?.coordinates;
            }
          } else {
            snackbar.showMessage({
              type: 'error',
              data: (
                <SnackbarApiErrorMessage errors={spRouteResponse?.errors} />
              ),
            });
          }
        }
      }
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={spResponse?.errors} />,
      });
    }
    return result;
  };

  const renderJobOnMap = async () => {
    await fetchAndSetServiceJob();
    mapRef?.current?.renderJob({ setActiveTab });
  };

  const renderServiceProvidersOnMap = async () => {
    setSpLoading(true);
    const result = await fetchServiceProviderData();
    mapRef?.current?.setServiceProviders({
      serviceProviders: result?.serviceProviders,
    });
    mapRef?.current?.renderServiceProviders();
    if (
      result?.routeGeometry &&
      result?.serviceProviders?.some(sp => isAssignedOrAcceptedSP(sp))
    ) {
      mapRef?.current?.setAcceptedAssignedSPSJRoute({
        route: result?.routeGeometry,
      });
    } else {
      mapRef?.current?.removeAcceptedAssignedSPSJRoute();
    }
    setPagination(result?.pagination);
    setServiceProviders(result?.serviceProviders);
    setSpLoading(false);
    mapRef?.current?.adjustMap();
  };

  const onMapReady = async mapObj => {
    setMap(mapObj);
    mapRef.current = new MapLib({
      map: mapObj,
      zoom: defaults.zoom,
      speed: defaults.speed,
      theme,
      marker: {
        job: null,
        serviceProviders: [],
        selectedServiceProvider: null,
      },
      onServiceProviderClick,
      isAssignedOrAcceptedSP,
    });
  };

  useEffect(() => {
    if (map) {
      renderJobOnMap();
    }
  }, [map, mapId]);

  useEffect(() => {
    if (jobInfo) {
      renderServiceProvidersOnMap();
    }
  }, [
    jobInfo,
    dispatchEventStatus,
    searchText,
    serviceDate,
    mapId,
    sortOption,
    pagination.page,
  ]);

  useEffect(() => {
    closeServiceProviderPopover();
  }, [searchText, dispatchEventStatus, sortOption, pagination.page]);

  useEffect(() => {
    setDispatchEventStatus(dispatchStatusConfig[0].value);
  }, [searchText, sortOption, pagination.page]);

  // This is to open SP location if it was passed in url
  useEffect(() => {
    if (
      locationIdFromUrlProcessedRef.current ||
      !locationIdFromUrl ||
      !serviceProviders.length
    ) {
      return;
    }

    locationIdFromUrlProcessedRef.current = true;
    const dataFound = serviceProviders.reduce((found, sp) => {
      if (found) return found;
      const location = sp.locations.find(l => l.id === locationIdFromUrl);
      return location ? { sp, location } : null;
    }, null);
    if (dataFound) {
      onServiceProviderClick({
        sp: dataFound.sp,
        location: dataFound?.location,
      });
    }
  }, [serviceProviders, locationIdFromUrl]);

  return (
    <LayoutFullWidth>
      <MapSupportCheckProvider>
        <Popover
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          slotProps={{
            root: {
              sx: {
                pointerEvents: 'none',
              },
            },
            paper: {
              sx: {
                pointerEvents: 'auto',
                maxWidth: 420,
                width: '100%',
              },
            },
          }}
          open={spPopOver.open}
          onClose={closeServiceProviderPopover}
          anchorReference="anchorPosition"
          anchorPosition={{ top: 80, left: window.innerWidth - 80 }}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}>
          <ServiceProviderInfo
            serviceProvider={selectedServiceProvider}
            currentJob={jobInfo}
            setDialog={setDialog}
            onClose={closeServiceProviderPopover}
            isAssignedOrAcceptedSP={isAssignedOrAcceptedSP}
          />
        </Popover>
        <JobMap
          maxBounds={northAmericaBounds}
          onReady={mapObj => onMapReady(mapObj)}
          {...defaults}
        />
        <LegendsWidget />
        <BackButton />
        {mapRef.current && (
          <CenterMapButton
            map={mapRef.current}
            lat={jobInfo?.locationLat}
            lng={jobInfo?.locationLng}
            zoom={defaults.zoom}
            disabled={spLoading || jobLoading}
          />
        )}
        <JobAndSPWidget
          sx={{ zIndex: themeobj => themeobj.zIndex.drawer + 10 }}
          jobInfo={jobInfo}
          jobLoading={jobLoading}
          spLoading={spLoading}
          mapRef={mapRef}
          serviceProviders={serviceProviders}
          setActiveTab={setActiveTab}
          setSearchText={setSearchText}
          setDispatchEventStatus={setDispatchEventStatus}
          setServiceDate={setServiceDate}
          setPagination={setPagination}
          activeTab={activeTab}
          searchText={searchText}
          dispatchEventStatus={dispatchEventStatus}
          serviceDate={serviceDate}
          pagination={pagination}
          dispatchStatusConfig={dispatchStatusConfig}
          setDialog={setDialog}
          onServiceProviderClick={onServiceProviderClick}
          isAssignedOrAcceptedSP={isAssignedOrAcceptedSP}
          closeServiceProviderPopover={closeServiceProviderPopover}
          sortOptions={SORT_OPTIONS(formatMessage)}
          sortOption={sortOption}
          setSortOption={setSortOption}
        />
        {dialog === 'ASSIGN' && (
          <AssignJobDialog
            key={dialog === 'ASSIGN' ? 'ASSIGN1' : 'ASSIGN2'}
            open={dialog === 'ASSIGN'}
            onClose={refreshId => {
              setDialog('NONE');
              if (refreshId !== null) {
                setMapId(refreshId);
              }
            }}
            jobInfo={jobInfo}
            selectedServiceProvider={selectedServiceProvider}
          />
        )}

        {dialog === 'MESSAGE' && (
          <SendMessageDialog
            key={dialog === 'MESSAGE' ? 'MESSAGE1' : 'MESSAGE2'}
            open={dialog === 'MESSAGE'}
            onClose={() => setDialog('NONE')}
            selectedServiceProvider={selectedServiceProvider}
            locationId={selectedServiceProvider?.location?.id}
          />
        )}

        {dialog === 'CANCEL' && (
          <CancelJobDialog
            key={dialog === 'CANCEL' ? 'CANCEL1' : 'CANCEL2'}
            open={dialog === 'CANCEL'}
            onClose={refreshId => {
              setDialog('NONE');
              if (refreshId !== null) {
                setMapId(refreshId);
              }
            }}
            recordVersion={jobInfo?.recordVersion}
            jobId={jobInfo?.id}
            jobNumber={jobInfo?.serviceJobNumber}
            jobStatus={jobInfo?.serviceJobStatus}
          />
        )}
        {dialog === 'RESCHEDULE' && (
          <RescheduleJobDialog
            key={dialog === 'RESCHEDULE' ? 'RESCHEDULE1' : 'RESCHEDULE2'}
            open={dialog === 'RESCHEDULE'}
            onClose={refreshId => {
              setDialog('NONE');
              if (refreshId !== null) {
                setMapId(refreshId);
              }
            }}
            initialData={jobInfo}
            recordVersion={jobInfo?.recordVersion}
            jobId={jobInfo?.id}
            jobNumber={jobInfo?.serviceJobNumber}
            jobStatus={jobInfo?.serviceJobStatus}
            providerId={jobInfo?.serviceProvider?.id}
            permissions={{
              customerServiceRequests: {
                requestDate: 'WRITE',
                afterHour: 'WRITE',
                beforeHour: 'WRITE',
              },
            }}
          />
        )}
      </MapSupportCheckProvider>
    </LayoutFullWidth>
  );
}

export default observer(ServiceJobMap);
